/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2021
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Button } from 'carbon-components-react';
import cx from 'classnames';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../../../store';
import { Noop } from '../../../../ts';
import { PlanName } from '../../../../ts/plan';
import { isManagerOfInstance } from '../../store/access-control/selectors';
import { selectPlan } from '../../store/instance/selectors';
import * as planActions from '../../store/plan/actions';
import { ctaEvents } from '../../util/trackers/segment';
import { UpgradeButtonMessages as messages } from './messages';

import './UpgradeButton.scss';

export interface UpgradeButtonProps {
  id?: string;
  size?: string;
  kind?: 'primary' | 'secondary' | 'tertiary' | 'ghost';
  className?: string;
  renderIcon?: any;
  buttonClassName?: string;
  planName?: PlanName;
  noTrial?: boolean;
  text?: string;
  onClick?: Noop;
  featureName: string;
  upgradeTo?: string;
}
export const UpgradeButton: React.FC<UpgradeButtonProps> = ({
  id,
  className,
  buttonClassName,
  planName,
  text,
  kind,
  size,
  featureName,
  noTrial,
  onClick,
  upgradeTo,
  renderIcon
}) => {
  const dispatch = useAppDispatch();

  const calculatedPlanName = useAppSelector(state =>
    selectPlan(state, planName)
  );
  const canUpdateInstance = useAppSelector(isManagerOfInstance);
  const intl = useIntl();
  const buttonText = messages[text?.toLowerCase()]
    ? intl.formatMessage(messages[text.toLowerCase()])
    : text || intl.formatMessage(messages.trial);

  const isDisabled = !canUpdateInstance || !calculatedPlanName;

  const handleButtonClick = useCallback(() => {
    ctaEvents.upgrade({
      successFlag: true,
      resultValue: 'Upgrade Modal Shown',
      category: featureName,
      uiElement: `button ${buttonText}`
    });

    dispatch(
      planActions.showUpgradeModal(
        calculatedPlanName,
        featureName,
        noTrial,
        upgradeTo
      )
    );
    onClick();
  }, [
    buttonText,
    calculatedPlanName,
    dispatch,
    featureName,
    noTrial,
    onClick,
    upgradeTo
  ]);

  return (
    <span className={cx(className, 'upgrade-button')}>
      <Button<any>
        id={id}
        size={size}
        className={cx('upgrade-button_button', buttonClassName)}
        title={
          isDisabled ? intl.formatMessage(messages.notManager) : buttonText
        }
        kind={kind}
        onClick={handleButtonClick}
        disabled={isDisabled}
        renderIcon={renderIcon}
        aria-haspopup="true">
        {buttonText}
      </Button>
    </span>
  );
};

UpgradeButton.defaultProps = {
  kind: 'primary',
  size: 'default',
  onClick: () => {}
};

export const UpgradeFeature = {
  DISAMBIGUATION: 'Disambiguation',
  VERSIONS: 'Versioning',
  INTERCOM: 'Intercom Integration',
  ACTIONS: 'Actions Skill',
  SEARCH: 'Search Skill',
  NAVIGATION: 'Navigation',
  HEADER: 'Page Header',
  TASK_LIST: 'Task List',
  SKILL_LIMIT: 'Skills limit',
  API_LIMIT: 'API limit',
  TRIAL_LIMIT: 'Trial limit',
  ENTITY_LIMIT: 'Entities limit',
  INTENT_LIMIT: 'Intents limit',
  DIALOG_LIMIT: 'Dialog nodes limit',
  WAVI: 'Voice Telephony and Text Messaging Integration',
  WEB_CHAT: 'Web chat integration',
  WHATSAPP: 'WhatsApp integration',
  NOTIFICATION_BAR: 'Notification banner',
  INACTIVITY_TIMEOUT: 'Inactivity timeout',
  VOICE: 'Voice Telephony Integration',
  TWILIO: 'Twilio Text Integration',
  MSG_LOG_WEBHOOK: 'Message log webhooks'
};
